import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import type { UploadFile } from 'antd/lib/upload/interface';
import { AppDispatch } from 'core/types';
import { loadVisitRequestForm, updateVisitRequestForm, uploadAttachment } from 'core/visitRequestForm/visitRequestFormApi';
import { loadGuestStep, updateGuestStep, submitGuestBooking, uploadGuestAttachment } from 'core/guestBooking/guestBookingApi';

/**
 * Centralises the "authenticated request" vs "guest (token) booking" decision so
 * the shared step pages can serve both flows. When a `:token` route param is
 * present we're in guest mode; otherwise the existing authenticated behaviour is
 * used unchanged (default), keeping the live flow untouched.
 */
const useRequestFlow = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { id, token } = useParams<{ id?: string, token?: string }>();
    const guestMode = Boolean(token);

    const loadStep = (step: number, amending = false) => (
        guestMode
            ? dispatch(loadGuestStep({ token: token as string, step }))
            : dispatch(loadVisitRequestForm({ id: id as string, step, amending }))
    );

    const updateStep = (step: number, data: object) => (
        guestMode
            ? dispatch(updateGuestStep({ token: token as string, data }))
            : dispatch(updateVisitRequestForm({ id: id as string, step, data }))
    );

    const submitReview = (declaration: object, amending = false) => (
        guestMode
            ? dispatch(submitGuestBooking({ token: token as string, declaration }))
            : dispatch(updateVisitRequestForm({
                id: id as string,
                step: 6,
                data: { declaration, amending, forcedStep: 6 },
            }))
    );

    const addAttachment = (attachment: UploadFile) => (
        guestMode
            ? dispatch(uploadGuestAttachment({ token: token as string, attachment }))
            : dispatch(uploadAttachment({ id: id as string, attachment }))
    );

    const goToStep = (step: number, state?: object) => (
        guestMode
            ? navigate(`/booking/${token}/step/${step}`, { state })
            : navigate(`/visitor-request/${id}/step/${step}`, { state })
    );

    const goToStart = () => (
        guestMode ? navigate('/') : navigate('/dashboard', { state: null })
    );

    return { guestMode, id, token, loadStep, updateStep, submitReview, addAttachment, goToStep, goToStart };
};

export default useRequestFlow;
